<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import { MAX_IP_ADDRESS_RESTRICTIONS, PRODUCT_NAME } from '@/constants';
import { DgrToggleButton } from '@stockmarkteam/donguri-ui';
import AdminBaseTable, { Field } from '@/components/admin/admin-base-table.vue';
import ActionMenu, {
  Action,
  type MenuItem,
} from '@/components/common/action-menu.vue';
import QuestionTooltip from '@/components/common/atoms/question-tooltip.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import AdminContent from '@/components/layouts/admin-content.vue';
import Header from '@/components/layouts/header.vue';
import { IpAddressRestriction } from '@/types';
import { useIpAddressRestrictions, useTeamInfo } from '@/utils/swr';
import { checkMoveToAdmin } from '@/utils/user';
import { useStore } from '@/utils/vue';
import IpAddressRestrictionBulkCreateModal from './ip-address-restriction-bulk-create-modal.vue';
import IpAddressRestrictionEditModal from './ip-address-restriction-edit-modal.vue';

type AdminTableItem = {
  id: number;
  ip_address: string;
  description: string;
};
const router = useRouter();
const store = useStore();
const { createSnackbar } = useSnackbar();

const { data: teamInfo, mutate: teamMutate } = useTeamInfo();
const enableIpAddressRestriction = ref(false);
watch(teamInfo, () => {
  if (!teamInfo.value) return;
  enableIpAddressRestriction.value =
    teamInfo.value.enable_ip_address_restriction;
});
const targetIpAddressRestriction = ref<IpAddressRestriction | undefined>(
  undefined,
);
const isEditModalOpen = ref(false);
onMounted(() => {
  checkMoveToAdmin(router);
});

const { data: ipAddressRestrictions, mutate: ipAddressRestrictionMutate } =
  useIpAddressRestrictions();
const ipAddresses = computed(
  () => ipAddressRestrictions.value?.ip_address_restrictions ?? [],
);
const toggleDisabled = computed(() => !ipAddresses.value.length);
const fields: Field<AdminTableItem>[] = [
  {
    name: 'ip_address',
    displayName: 'IPアドレス',
    isSortable: false,
    type: 'string',
    minWidth: 192,
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'description',
    displayName: '備考',
    isSortable: false,
    type: 'string',
    minWidth: 380,
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'actions',
    displayName: '',
    isSortable: false,
    type: 'string',
    fieldPosition: 'center',
    cellPosition: 'center',
  },
];
const items = computed<AdminTableItem[]>(() =>
  ipAddresses.value.map(ipAddressRestriction => ({
    ip_address: ipAddressRestriction.ip_address,
    description: ipAddressRestriction.description,
    id: ipAddressRestriction.id || 0,
  })),
);

const openEditModal = (item: AdminTableItem) => {
  targetIpAddressRestriction.value = ipAddresses.value.find(
    ipAddressRestriction => ipAddressRestriction.id === item.id,
  );

  isEditModalOpen.value = true;
};

const updateEnableIpAddressRestriction = () => {
  if (toggleDisabled.value) return;

  const updateSetting = enableIpAddressRestriction.value
    ? {
        headerText: 'IPアドレス制限を無効にしますか?',
        bodyText:
          'IPアドレス制限を無効にすると、外部からのアクセスが全て許可されます。',
        btnText: '無効化',
        failedMessage: 'IPアドレス制限の無効化に失敗しました',
        successMessage: 'IPアドレス制限を無効にしました',
      }
    : {
        headerText: 'IPアドレス制限を有効にしますか?',
        bodyText:
          'IPアドレス制限を有効にすると、追加したIPアドレス以外からのアクセスが制限されます。',
        btnText: '有効化',
        failedMessage: 'IPアドレス制限の有効化に失敗しました',
        successMessage: 'IPアドレス制限を有効にしました',
      };
  const updateAction = () => {
    try {
      api.putEnableIpAddressRestriction(!enableIpAddressRestriction.value);
      createSnackbar({
        message: updateSetting.successMessage,
        type: 'success',
      });
      enableIpAddressRestriction.value = !enableIpAddressRestriction.value;
    } catch (error) {
      createSnackbar({
        message: updateSetting.failedMessage,
        type: 'error',
      });
      throw error;
    }
  };

  const payload = {
    headerText: updateSetting.headerText,
    bodyText: updateSetting.bodyText,
    btnText: updateSetting.btnText,
    confirmationType: 'submit',
    action: updateAction,
  };
  store.commit('confirmationModal/showConfirmationModal');
  store.commit('confirmationModal/setTextAndAction', payload);
};

const deleteIpAddressRestriction = async (item: AdminTableItem) => {
  const deleteAction = async () => {
    await api.deleteIpAddressRestriction(item.id as number).catch(err => {
      createSnackbar({
        message: 'IPアドレスを削除できませんでした',
        type: 'error',
      });
      throw err;
    });
    createSnackbar({
      message: 'IPアドレスを削除しました',
      type: 'success',
    });
    ipAddressRestrictionMutate();
    teamMutate();
  };
  const payload = {
    headerText: 'IPアドレスを削除しますか？',
    bodyText:
      ipAddressRestrictions.value?.ip_address_restrictions.length === 1
        ? `${item.ip_address}を削除します。\nまた、削除すると自動でIPアドレス制限が無効化されます。`
        : `${item.ip_address}を削除します。`,
    action: deleteAction,
  };

  store.commit('confirmationModal/showConfirmationModal');
  store.commit('confirmationModal/setTextAndAction', payload);
};

const updateList = () => {
  ipAddressRestrictionMutate();
};

const actions: Array<MenuItem> = [
  {
    name: '編集',
    icon: 'pencil',
    isSeparator: false,
    func: openEditModal as Action['func'],
  },
  {
    name: 'separator',
    isSeparator: true,
  },
  {
    name: '削除',
    icon: 'trash',
    isSeparator: false,
    func: deleteIpAddressRestriction as Action['func'],
  },
];
</script>
<template>
  <div class="ip-address-restriction">
    <Header title="IPアドレス制限" header-width="100%" />
    <AdminContent class="ip-address-restriction-content">
      <div>
        <div class="header">
          <div class="title">
            <span class="c-title-bold--m">IPアドレス</span
            ><span class="count c-text-regular--s"
              >・{{ items.length }}/{{ MAX_IP_ADDRESS_RESTRICTIONS }}件</span
            >
          </div>

          <IpAddressRestrictionBulkCreateModal
            @on-submit="updateList"
            :current-ip-address-restrictions-count="items.length"
          />
        </div>
        <div class="spacing-16"></div>
        <div class="enable-ip-address-restriction-toggle">
          <div
            class="toggle-form"
            :class="{ 'toggle-disabled': toggleDisabled }"
          >
            <DgrToggleButton
              @click="updateEnableIpAddressRestriction"
              :model-value="enableIpAddressRestriction"
              :read-only="true"
              :disabled="toggleDisabled"
            >
            </DgrToggleButton>
            <div class="c-text-regular--s">IPアドレス制限を有効にする</div>
          </div>
          <div class="spacing-04"></div>

          <QuestionTooltip>
            <span>
              IPアドレス制限を有効にすると、登録したIPアドレスのみ、{{
                PRODUCT_NAME
              }}にアクセス可能となります。
            </span>
          </QuestionTooltip>
        </div>
        <div class="spacing-16"></div>
        <AdminBaseTable
          :items="items"
          :fields="fields"
          class="ip-address-table"
        >
          <template #header-ip_address="{ item }">
            <div class="title">
              <span class="c-text-regular--m">{{ item.displayName }}</span>
              <div class="spacing-04"></div>

              <QuestionTooltip>
                <span>
                  IPv4の通常の表記及びCIDR表記に対応しています。<br />通常の表記（例
                  203.0.113.0）<br />CIDR表記（例 203.0.113.0/24）
                </span>
              </QuestionTooltip>
            </div>
          </template>
          <template #cell-description="{ item }">
            {{ item.description }}
          </template>
          <template #cell-actions="{ item }">
            <ActionMenu
              :actions="
                actions.map(action =>
                  action.isSeparator
                    ? action
                    : {
                        ...action,
                        func: () => action.func?.(item),
                      },
                )
              "
            />
          </template>
          <template #empty-tbody>
            <div>
              登録されているIPアドレスがありません、IPアドレス追加から登録してください。
            </div>
          </template>
        </AdminBaseTable>
      </div>
    </AdminContent>
    <IpAddressRestrictionEditModal
      v-model:open="isEditModalOpen"
      :ip-address-restriction="targetIpAddressRestriction"
      @on-submit="updateList"
    />
  </div>
</template>
<style lang="scss" scoped>
.ip-address-restriction {
  width: 100%;
  .ip-address-restriction-content {
    display: flex;
    justify-content: center;
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .enable-ip-address-restriction-toggle {
      display: flex;
      justify-content: flex-end;
      .toggle-form {
        display: flex;
      }
      .toggle-disabled {
        color: $color-gray600;
      }
    }
    .ip-address-table {
      margin-bottom: 80px;
    }
  }
  .title {
    display: flex;
    align-items: center;
    .count {
      color: $color-gray600;
    }
  }
}
</style>
